
import BCXMessengerGroup from '@/components/messenger/Group/BCXMessengerGroup.vue';
import BCXMessengerChatGroupPreview from '@/components/messenger/ChatGroup/BCXMessengerChatGroupPreview.vue';
import BCXMessengerChatGroup from '@/components/messenger/ChatGroup/BCXMessengerChatGroup.vue';
import useMessengerState from '@/state/messenger/messengerState';
import {
  Chat, ChatGroup, CHAT_TYPE_DIRECT, CHAT_TYPE_FORUM, CHAT_TYPE_GROUP, CHAT_TYPE_PROJECT, Group
} from '@/models/Messenger';
import BCXMessengerGroupCollapsible from '@/components/messenger/Group/BCXMessengerGroupCollapsible.vue';
import {
  computed, defineComponent, provide
} from 'vue';
import { traverseMessagesInGroups } from '@/state/messenger/traverseHelpers';
import withNewCount from '@/utils/withNewCount';
import sortComp from '@/utils/sortComp';
import { last } from 'lodash';
import { debouncedRef, templateRef } from '@vueuse/core';
import BCXMessengerLoadingSkeleton from '@/components/messenger/Group/BCXMessengerLoadingSkeleton.vue';
import BCXMessengerChatPreview from '@/components/messenger/Chat/BCXMessengerChatPreview.vue';
import MarvinLookDown from '@/components/svg/marvin/marvin_look_down_straight.svg?inline';
import MarvinSad from '@/components/svg/marvin/marvin_sad.svg?inline';
import BCXLazy from '@/components/molecules/BCXLazy.vue';
import BCXMessengerChatPreviewLoader from '@/components/messenger/Chat/BCXMessengerChatPreviewLoader.vue';

export default defineComponent({
  name: 'BCXMessengerConversations',
  components: {
    BCXMessengerChatPreviewLoader,
    BCXMessengerChatPreview,
    BCXMessengerChatGroupPreview,
    BCXMessengerChatGroup,
    BCXMessengerLoadingSkeleton,
    BCXMessengerGroupCollapsible,
    BCXMessengerGroup,
    BCXLazy,
    MarvinLookDown,
    MarvinSad
  },
  setup() {
    const {
      groupsFilteredSorted: groups,
      isForumGroupOpened,
      isProjectGroupOpened,
      isChatGroupOpened,
      isMarkingChats,
      isLoading,
      markedChats,
      isLoadingFirstTime,
      isFlat,
      chatsSorted,
      sortBy,
      sortDir,
      searchTerm,
      showNewFilter,
      groupFilters,
      chats,
      chatgroups,
      groupLastMessages,
      perGroupMeta,
      removeMarkedChats,
    } = useMessengerState();

    provide('scrollBoundary', templateRef('groups'));

    const privateGroups = computed(() => groups.value.filter(({ type }: { type: string }) => type === CHAT_TYPE_DIRECT));
    const forumGroups = computed(() => groups.value.filter(({ type }: { type: string }) => type === CHAT_TYPE_FORUM));
    const projectGroups = computed(() => groups.value.filter(({ type }: { type: string }) => type === CHAT_TYPE_PROJECT));

    const shouldForumGroupsOpen = computed(() => forumGroups.value.some(({ shouldOpen }) => shouldOpen));
    const shouldProjectGroupsOpen = computed(() => projectGroups.value.some(({ shouldOpen }) => shouldOpen));

    const groupsUnreadMessages = (groups: Group[]) => {
      let unread = 0;
      traverseMessagesInGroups(groups, (message) => {
        if (!message.read) unread++;
      });
      return unread;
    };

    const forumGroupsUnreadMessages = computed(() => groupsUnreadMessages(forumGroups.value));
    const projectGroupsUnreadMessages = computed(() => groupsUnreadMessages(projectGroups.value));

    const isForumGroupReallyOpened = computed<boolean>({
      get: () => shouldForumGroupsOpen.value || isForumGroupOpened.value,
      set: (is) => {
        isForumGroupOpened.value = is;
      },
    });

    const isProjectGroupReallyOpened = computed<boolean>({
      get: () => shouldProjectGroupsOpen.value || isProjectGroupOpened.value,
      set: (is) => {
        isProjectGroupOpened.value = is;
      },
    });

    const showGroups = computed(() => groupFilters.all || groupFilters[CHAT_TYPE_GROUP]);
    const visibleChatgroups = computed(() => {
      if (!showGroups.value || showNewFilter.value) return [];
      const term = searchTerm.value.toLocaleLowerCase();
      return chatgroups.value.filter((group) => !term || group.title.toLocaleLowerCase().includes(term));
    });

    const shouldChatGroupsOpen = computed(() => !!searchTerm.value.length && !!visibleChatgroups.value.length);
    const isChatGroupReallyOpened = computed<boolean>({
      get: () => shouldChatGroupsOpen.value || isChatGroupOpened.value,
      set: (is) => {
        isChatGroupOpened.value = is;
      }
    });

    const chatgroupsUnreadMessages = computed(() => visibleChatgroups.value
      .reduce((sum, group) => sum + (perGroupMeta.value[group.groupId]?.unreadCount ?? 0), 0));

    // Flat view: merge chat groups into the chat list and sort them together,
    // using the same keys as useSorter (name / last message time). Time is
    // normalized to a timestamp because chats and groups use different date formats.
    type FlatItem = { chat?: Chat; group?: ChatGroup };
    const flatItems = computed<FlatItem[]>(() => {
      const items: FlatItem[] = [
        ...chatsSorted.value.map((chat) => ({ chat })),
        ...visibleChatgroups.value.map((group) => ({ group }))
      ];
      const timeOf = (item: FlatItem): number => {
        const created = item.group
          ? (groupLastMessages.value[item.group.groupId]?.created ?? item.group.created)
          : last(item.chat?.messages)?.created;
        return created ? new Date(created).getTime() : 0;
      };
      const nameOf = (item: FlatItem): string => (item.group
        ? item.group.title
        : `${item.chat?.privatePartner?.firstname ?? ''} ${item.chat?.privatePartner?.lastname ?? ''}`);
      return items.sort((a, b) => {
        let result = sortBy.value === 'user'
          ? sortComp(nameOf(b), nameOf(a))
          : sortComp(timeOf(b), timeOf(a));
        if (sortDir.value === 'ascending') result = -result;
        return result;
      });
    });

    const noResults = computed(() => {
      const resultsAvailable = !!chats.value.length || !!chatgroups.value.length;
      const filtersActive = !!searchTerm.value || showNewFilter.value || !groupFilters.all;
      const noChatEntries = isFlat.value ? !chatsSorted.value.length
        : !forumGroups.value.length && !privateGroups.value.length && !projectGroups.value.length;

      return resultsAvailable && filtersActive && noChatEntries && !visibleChatgroups.value.length;
    });

    const noChats = computed(() => !chats.value.length && !chatgroups.value.length);

    return {
      isMarkingChats,
      isLoading: debouncedRef(isLoading, 500),
      privateGroups,
      forumGroups,
      projectGroups,
      isForumGroupReallyOpened,
      isProjectGroupReallyOpened,
      isChatGroupReallyOpened,
      forumGroupsUnreadMessages,
      projectGroupsUnreadMessages,
      chatgroupsUnreadMessages,
      withNewCount,
      removeMarkedChats,
      markedChats,
      isLoadingFirstTime,
      noResults,
      isFlat,
      flatItems,
      visibleChatgroups,
      noChats
    };
  },
});
